// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// types
import type {
  AutomaticRosteringQuestionTurn,
  AutomaticRosteringUnavailableReason
} from 'generated';
import type { AutomaticRosteringChatMessage } from '../automaticRostering.types';

// utils
import { mutations } from 'RestAPIs/rapini';
import { v4 as uuidv4 } from 'uuid';

/** The engine accepts at most twelve earlier turns; sending more is a 400. */
const MAX_HISTORY_TURNS = 12;

/**
 * And at most two thousand characters per turn, which the api contract enforces before the request
 * ever leaves SDB Planning (`AutomaticRosteringQuestionTurn.content`). An engine answer is written by
 * a language model and regularly runs longer than that, so a turn is cut down to size rather than
 * replayed whole - the alternative is a 400 the planner reads as "the engine is unreachable", on
 * every follow-up question until the oversized turn ages out of the window.
 */
const MAX_HISTORY_TURN_CHARS = 2000;

type UseArExplanationProps = {
  organizationalUnitCode: string | null;
  startDate: string;
  endDate: string;
};

/**
 * The conversation about the calculation for one week.
 *
 * <p>The engine keeps no session, so every question carries the turns that led to it. It answers the
 * last twelve at most, and the oldest are the ones dropped — a follow-up needs what came just before
 * it, not what opened the window.
 *
 * <p>An answer the engine could not give is still an answer: it says so in Dutch and carries the
 * reason. Only a transport failure produces an error message of our own.
 */
const useAutomaticRosteringExplanation = ({
  organizationalUnitCode,
  startDate,
  endDate
}: UseArExplanationProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan.automaticRostering'
  });
  const [messages, setMessages] = useState<AutomaticRosteringChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const unansweredText = (reason?: AutomaticRosteringUnavailableReason) =>
    reason ? t(`chat.unavailable.${reason}`) : t('chat.noAnswer');

  const askMutation = mutations.useAskAutomaticRostering({
    onSuccess: (answer) => {
      setMessages((current) => [
        ...current,
        {
          id: uuidv4(),
          role: 'assistant',
          // The contract has `answer` required, but an engine that answers 200 with nothing in it
          // would otherwise leave an empty bubble on screen and, worse, an empty turn in the history
          // that the next question is refused for.
          text: answer.answer?.trim()
            ? answer.answer
            : unansweredText(answer.reason),
          unavailableReason: answer.available ? undefined : answer.reason
        }
      ]);
      setSuggestions(answer.suggestions ?? []);
    },
    onError: () => {
      setMessages((current) => [
        ...current,
        { id: uuidv4(), role: 'assistant', text: t('chat.failed') }
      ]);
    }
  });

  const askQuestion = (question: string) => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || !organizationalUnitCode || askMutation.isLoading) {
      return;
    }

    const history = toHistory(messages);
    setMessages((current) => [
      ...current,
      { id: uuidv4(), role: 'user', text: trimmedQuestion }
    ]);
    setSuggestions([]);

    askMutation.mutate({
      organizationalUnitCode,
      startDate,
      endDate,
      message: trimmedQuestion,
      history
    });
  };

  return {
    messages,
    suggestions,
    isAnswering: askMutation.isLoading,
    askQuestion
  };
};

const toHistory = (
  messages: AutomaticRosteringChatMessage[]
): AutomaticRosteringQuestionTurn[] =>
  messages
    .slice(-MAX_HISTORY_TURNS)
    .map((message) => ({
      role:
        message.role === 'user' ? ('USER' as const) : ('ASSISTANT' as const),
      content: toHistoryContent(message.text)
    }))
    .filter(({ content }) => content.length > 0);

/** The last character makes room for the ellipsis, so the result is never over the limit. */
const toHistoryContent = (text: string): string => {
  const trimmed = text.trim();

  return trimmed.length > MAX_HISTORY_TURN_CHARS
    ? `${trimmed.slice(0, MAX_HISTORY_TURN_CHARS - 1)}…`
    : trimmed;
};

export default useAutomaticRosteringExplanation;
